/**
 * Screen 03 — the flavour wheel, drawn from the flavour database to match the
 * reference wheel its data came with.
 *
 * Up to three ingredients over each other on one wheel: from the bar's own
 * library (anything linked to the flavour database, and house batches blended from
 * their build), or any ingredient the flavour database knows, found by name. All notes
 * mode draws every note with a radar line per ingredient; Key notes draws
 * bars for just the notes that matter. Beside it: each ingredient's strongest
 * notes, a card for the note tapped, what the flavour database says goes with the
 * first pick (the bar's own stock first), real cocktails that use the
 * selection, synergy when that is switched on, and — for someone who may
 * edit ingredients — the pick's link to the flavour database, to confirm or set.
 *
 * The figure is `FlavourWheel` and the maths `@baros/core`'s wheel.ts; this
 * file fetches, holds the selection and lays out the panel.
 */

import { hasFeature } from "../features.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  developmentPrompt,
  flavourColour,
  flavourNameKey,
  flavourRefsFor,
  FLAVOUR_NOTES,
  ingredientColour,
  isBatchName,
  MAX_WHEEL_INGREDIENTS,
  noteVerdict,
  resolveFlavour,
  sharedActiveNotes,
  strongestCount,
  topFlavourNotes,
  type FlavourCocktail,
  type FlavourPairing,
  type FlavourProfile,
  type Ingredient,
  type ResolvedFlavour,
  type Spec,
  type WheelMode,
} from "@baros/core";
import { useApp } from "../context.js";
import type { ScreenProps } from "../screens.js";
import { FlavourWheel } from "./FlavourWheel.js";
import { flavourApi, searchFlavourNames, type SynergyResult } from "./flavour-api.js";
import "./wheel.css";

/** A pick: one of the bar's ingredients, or a flavour-database name the bar may not hold. */
type Pick = { readonly kind: "library"; readonly ingredientId: string } | { readonly kind: "database"; readonly name: string };

const pickKey = (p: Pick) => (p.kind === "library" ? `ing:${p.ingredientId}` : `fl:${flavourNameKey(p.name)}`);

/** A pick with everything the screen knows about it. */
interface Selected {
  readonly pick: Pick;
  readonly key: string;
  readonly label: string;
  readonly colour: string;
  /** The flavour-database name to ask about pairings, cocktails and synergy; null for a batch. */
  readonly ref: string | null;
  readonly ingredient: Ingredient | null;
  /** Whether the bar holds it: a library pick, or a name some library ingredient links to. */
  readonly held: boolean;
  readonly resolved: ResolvedFlavour | null;
  readonly loading: boolean;
}

const COCKTAIL_PAGE = 10;

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function WheelScreen({ params }: ScreenProps) {
  const app = useApp();
  const token = app.token;

  const ingredients = useMemo(
    () => new Map<string, Ingredient>(app.library.ingredients.map((i) => [i.ingredientId, i])),
    [app.library.ingredients],
  );
  const specs = useMemo(() => new Map<string, Spec>(app.library.specs.map((s) => [s.specId, s])), [app.library.specs]);
  /** Library ingredients by the flavour-database name they link to. */
  const heldByRef = useMemo(() => {
    const out = new Map<string, Ingredient[]>();
    for (const ing of app.library.ingredients) {
      if (!ing.flavour) continue;
      const k = flavourNameKey(ing.flavour.ref);
      out.set(k, [...(out.get(k) ?? []), ing]);
    }
    return out;
  }, [app.library.ingredients]);

  // ------------------------------------------------------------- selection
  const [picks, setPicks] = useState<readonly Pick[]>(() =>
    params.ingredientId ? [{ kind: "library", ingredientId: params.ingredientId }] : [],
  );
  const [mode, setMode] = useState<WheelMode>("all");
  const [note, setNote] = useState<number | null>(null);
  const [picking, setPicking] = useState(false);
  /** The library ingredient whose link is being set, when the link search is open. */
  const [linking, setLinking] = useState<string | null>(null);

  const addPick = useCallback((pick: Pick) => {
    setPicks((list) =>
      list.length >= MAX_WHEEL_INGREDIENTS || list.some((p) => pickKey(p) === pickKey(pick)) ? list : [...list, pick],
    );
  }, []);

  // -------------------------------------------------------------- profiles
  /** The flavour database's profiles fetched so far, by `flavourNameKey`; null marks a name it does not hold. */
  const [profiles, setProfiles] = useState<ReadonlyMap<string, FlavourProfile | null>>(new Map());
  const asked = useRef(new Set<string>());
  const [loadError, setLoadError] = useState<string | null>(null);

  const ensureProfiles = useCallback(
    async (refs: readonly string[]) => {
      const missing = refs.filter((r) => !asked.current.has(flavourNameKey(r)));
      if (missing.length === 0) return;
      missing.forEach((r) => asked.current.add(flavourNameKey(r)));
      try {
        const found = await flavourApi.profiles(token, missing);
        setProfiles((prev) => {
          const next = new Map(prev);
          for (const r of missing) if (!next.has(flavourNameKey(r))) next.set(flavourNameKey(r), null);
          for (const p of found) next.set(flavourNameKey(p.name), p);
          return next;
        });
      } catch (err) {
        missing.forEach((r) => asked.current.delete(flavourNameKey(r)));
        setLoadError(errorText(err, "Could not load the flavour data"));
      }
    },
    [token],
  );

  /** Only real profiles, for `resolveFlavour`. */
  const known = useMemo(() => {
    const out = new Map<string, FlavourProfile>();
    for (const [k, p] of profiles) if (p) out.set(k, p);
    return out;
  }, [profiles]);

  const neededRefs = useMemo(
    () => [
      ...flavourRefsFor(
        picks.flatMap((p) => (p.kind === "library" ? [p.ingredientId] : [])),
        ingredients,
        specs,
      ),
      ...picks.flatMap((p) => (p.kind === "database" ? [p.name] : [])),
    ],
    [picks, ingredients, specs],
  );
  useEffect(() => {
    void ensureProfiles(neededRefs);
  }, [neededRefs, ensureProfiles]);

  const selected = useMemo<Selected[]>(
    () =>
      picks.map((pick, i) => {
        const colour = ingredientColour(i);
        if (pick.kind === "library") {
          const ing = ingredients.get(pick.ingredientId) ?? null;
          const refs = flavourRefsFor([pick.ingredientId], ingredients, specs);
          return {
            pick,
            key: pickKey(pick),
            label: ing?.name ?? pick.ingredientId,
            colour,
            ref: ing?.flavour?.ref ?? null,
            ingredient: ing,
            held: true,
            resolved: resolveFlavour(pick.ingredientId, ingredients, specs, known),
            loading: refs.some((r) => !profiles.has(flavourNameKey(r))),
          };
        }
        const profile = profiles.get(flavourNameKey(pick.name));
        return {
          pick,
          key: pickKey(pick),
          label: profile?.name ?? pick.name,
          colour,
          ref: profile?.name ?? pick.name,
          ingredient: null,
          held: heldByRef.has(flavourNameKey(pick.name)),
          resolved: profile ? { profile, from: "link", coverage: 1 } : null,
          loading: profile === undefined,
        };
      }),
    [picks, ingredients, specs, known, profiles, heldByRef],
  );
  const drawn = selected.filter((s): s is Selected & { resolved: ResolvedFlavour } => s.resolved !== null);
  const refs = [...new Map(selected.flatMap((s) => (s.ref ? [[flavourNameKey(s.ref), s.ref] as const] : []))).values()];
  const refsKey = JSON.stringify(refs);

  // ------------------------------------------------------------- goes with
  const firstRef = selected[0]?.ref ?? null;
  const [pairings, setPairings] = useState<{ ref: string; list: readonly FlavourPairing[] } | null>(null);
  const [pairingsError, setPairingsError] = useState<string | null>(null);
  useEffect(() => {
    setPairingsError(null);
    if (!firstRef) {
      setPairings(null);
      return;
    }
    let live = true;
    flavourApi
      .ingredient(token, firstRef)
      .then((info) => live && setPairings({ ref: firstRef, list: info.pairings }))
      .catch((err: unknown) => live && setPairingsError(errorText(err, "Could not load pairings")));
    return () => {
      live = false;
    };
  }, [token, firstRef]);

  const goesWith = useMemo(() => {
    if (!pairings || pairings.ref !== firstRef) return [];
    const rows = pairings.list.map((p) => ({ ...p, held: heldByRef.get(flavourNameKey(p.name)) ?? [] }));
    // The bar's own stock first; otherwise the flavour database's order (strongest synergy first).
    return [...rows.filter((r) => r.held.length > 0), ...rows.filter((r) => r.held.length === 0)];
  }, [pairings, firstRef, heldByRef]);

  // ------------------------------------------------------------- cocktails
  const [cocktails, setCocktails] = useState<{ key: string; total: number; list: readonly FlavourCocktail[] } | null>(null);
  const [cocktailsBusy, setCocktailsBusy] = useState(false);
  const [cocktailsError, setCocktailsError] = useState<string | null>(null);
  const loadCocktails = useCallback(
    async (names: readonly string[], offset: number) => {
      const key = JSON.stringify(names);
      setCocktailsBusy(true);
      setCocktailsError(null);
      try {
        const page = await flavourApi.cocktails(token, names, COCKTAIL_PAGE, offset);
        setCocktails((prev) => ({
          key,
          total: page.total,
          list: offset > 0 && prev?.key === key ? [...prev.list, ...page.cocktails] : page.cocktails,
        }));
      } catch (err) {
        setCocktailsError(errorText(err, "Could not load cocktails"));
      } finally {
        setCocktailsBusy(false);
      }
    },
    [token],
  );
  useEffect(() => {
    const names = JSON.parse(refsKey) as string[];
    setCocktails(null);
    if (names.length > 0) void loadCocktails(names, 0);
  }, [refsKey, loadCocktails]);

  // --------------------------------------------------------------- synergy
  const [synergy, setSynergy] = useState<{ key: string; result: SynergyResult | "error" } | null>(null);
  useEffect(() => {
    const names = JSON.parse(refsKey) as string[];
    if (names.length < 2) {
      setSynergy(null);
      return;
    }
    let live = true;
    flavourApi
      .synergy(token, names)
      .then((result) => live && setSynergy({ key: refsKey, result }))
      .catch(() => live && setSynergy({ key: refsKey, result: "error" }));
    return () => {
      live = false;
    };
  }, [token, refsKey]);

  // ------------------------------------------------------------------ links
  const canEdit = Boolean(app.me.can["ingredient:edit"]);
  const [linkBusy, setLinkBusy] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  async function saveLink(ingredient: Ingredient, ref: string) {
    setLinkBusy(ingredient.ingredientId);
    setLinkError(null);
    try {
      const overrides = ingredient.flavour?.overrides;
      await flavourApi.link(token, ingredient.ingredientId, overrides ? { ref, overrides } : { ref });
      await app.refreshLibrary();
    } catch (err) {
      setLinkError(errorText(err, "Could not save the link"));
    } finally {
      setLinkBusy(null);
    }
  }

  // ------------------------------------------------------------------ names
  const [allNames, setAllNames] = useState<readonly string[] | null>(null);
  const [namesError, setNamesError] = useState<string | null>(null);
  const searching = picking || linking !== null;
  useEffect(() => {
    if (!searching || allNames) return;
    let live = true;
    flavourApi
      .names(token)
      .then((r) => live && setAllNames(r.names))
      .catch((err: unknown) => live && setNamesError(errorText(err, "Could not load the flavour database's names")));
    return () => {
      live = false;
    };
  }, [searching, allNames, token]);

  // While choosing, fetch what every library ingredient needs, so the list
  // offers only those whose flavour actually resolves.
  const libraryRefs = useMemo(
    () =>
      picking
        ? flavourRefsFor(
            app.library.ingredients.map((i) => i.ingredientId),
            ingredients,
            specs,
          )
        : [],
    [picking, app.library.ingredients, ingredients, specs],
  );
  useEffect(() => {
    if (libraryRefs.length) void ensureProfiles(libraryRefs);
  }, [libraryRefs, ensureProfiles]);
  const libraryLoading = libraryRefs.some((r) => !profiles.has(flavourNameKey(r)));

  // ------------------------------------------------------------------ prompt
  const shared = sharedActiveNotes(drawn.map((d) => d.resolved.profile)).map((i) => FLAVOUR_NOTES[i]!.label);
  const prompt = developmentPrompt(
    drawn.map((d) => d.label),
    shared,
  );

  const noteInfo = note !== null ? FLAVOUR_NOTES[note] : undefined;
  const full = picks.length >= MAX_WHEEL_INGREDIENTS;

  return (
    <div className="wheel-screen">
      <section className="wheel-main">
        <div className="wheel-tools">
          <span className="spacer" />
          <div className="wheel-mode" role="group" aria-label="Wheel mode">
            <button className="btn sm" aria-pressed={mode === "key"} onClick={() => setMode("key")}>
              Key notes
            </button>
            <button className="btn sm" aria-pressed={mode === "all"} onClick={() => setMode("all")}>
              All notes
            </button>
          </div>
        </div>

        <div className="wheel-picks">
          {selected.map((s) => (
            <div key={s.key} className="wheel-pick" style={{ borderColor: s.colour }}>
              <span className="swatch" style={{ background: s.colour }} />
              <span className="body">
                <span className="n">{s.label}</span>
                <span className="s">{pickStatus(s)}</span>
              </span>
              <button
                className="remove"
                aria-label={`Remove ${s.label}`}
                onClick={() => {
                  setPicks((list) => list.filter((p) => pickKey(p) !== s.key));
                  setNote(null);
                }}
              >
                ×
              </button>
            </div>
          ))}
          {!full && (
            <button className="btn dashed wheel-add" onClick={() => setPicking(true)}>
              + {picks.length === 0 ? "Choose an ingredient" : "Add an ingredient"}
            </button>
          )}
        </div>

        {loadError && <div className="msg err">{loadError}</div>}

        <div className="wheel-figure">
          <FlavourWheel
            items={drawn.map((d) => ({ key: d.key, label: d.label, colour: d.colour, profile: d.resolved.profile }))}
            mode={mode}
            selectedNote={note}
            onNote={(i) => setNote((current) => (current === i ? null : i))}
          />
        </div>

        <div className="wheel-hints">
          <span className="label label-sm">
            {mode === "all"
              ? "Bold notes score over 2 of 10 for an ingredient · tap a note for its card"
              : "Notes over 1 of 10 for an ingredient · one bar per ingredient that rates it"}
          </span>
        </div>
      </section>

      <aside className="wheel-panel">
        {noteInfo && (
          <section className="wheel-card">
            <div className="wheel-card-head">
              <span className="wheel-badge" style={{ background: flavourColour(noteInfo.family) }}>
                {noteInfo.family.toUpperCase()}
              </span>
              <span className="spacer" />
              <button className="wheel-close" aria-label="Close the note card" onClick={() => setNote(null)}>
                ×
              </button>
            </div>
            <h3>{noteInfo.label}</h3>
            {noteInfo.label !== noteInfo.name && <div className="label label-sm">{noteInfo.name}</div>}
            {drawn.map((d) => {
              const v = d.resolved.profile.notes[noteInfo.index] ?? 0;
              return (
                <div key={d.key} className="wheel-score">
                  <span className="n">{d.label}</span>
                  <span className="bar">
                    <span style={{ width: `${v * 10}%`, background: d.colour }} />
                  </span>
                  <span className="v">{v.toFixed(1)}</span>
                </div>
              );
            })}
            {drawn.length > 0 && (
              <p className="wheel-verdict">
                {noteVerdict(
                  noteInfo.index,
                  drawn.map((d) => ({ name: d.label, profile: d.resolved.profile })),
                )}
              </p>
            )}
          </section>
        )}

        <section className="wheel-section">
          <div className="label">Strongest notes</div>
          {drawn.length === 0 && <div className="wheel-empty">Choose an ingredient to begin</div>}
          {drawn.map((d) => (
            <div key={d.key} className="wheel-strongest">
              <div className="n" style={{ color: d.colour }}>
                {d.label}
              </div>
              <div className="wheel-chips">
                {topFlavourNotes(d.resolved.profile, strongestCount(drawn.length), 0).map((n) => (
                  <button key={n.index} className="wheel-chip" aria-pressed={note === n.index} onClick={() => setNote(n.index)}>
                    <span className="dot" style={{ background: flavourColour(n.family) }} />
                    {n.label} <span className="muted">{n.value.toFixed(1)}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </section>

        {canEdit &&
          selected.map((s) => {
            const ing = s.ingredient;
            if (!ing || s.pick.kind !== "library") return null;
            const batch = specs.has(ing.ingredientId) || isBatchName(ing.name);
            if (batch && !ing.flavour) return null;
            const link = ing.flavour;
            const busy = linkBusy === ing.ingredientId;
            return (
              <section key={`link:${s.key}`} className="wheel-section wheel-link">
                <div className="label">Flavour link · {ing.name}</div>
                {link ? (
                  <p>
                    Linked to <strong>{link.ref}</strong>{" "}
                    <span className="muted">
                      ({link.status}
                      {link.status === "suggested" && link.how ? ` · ${link.how}` : ""})
                    </span>
                  </p>
                ) : (
                  <p>Not linked</p>
                )}
                <div className="row">
                  {link?.status === "suggested" && (
                    <button className="btn sm" disabled={busy} onClick={() => void saveLink(ing, link.ref)}>
                      Confirm
                    </button>
                  )}
                  <button className="btn sm" disabled={busy} onClick={() => setLinking(ing.ingredientId)}>
                    {link ? "Change" : "Find in the flavour database"}
                  </button>
                </div>
              </section>
            );
          })}
        {linkError && <div className="msg err">{linkError}</div>}

        <section className="wheel-section">
          <div className="label">
            Goes with{selected[0] ? ` · ${selected[0].label}` : ""}
            {full ? "" : " · tap to add"}
          </div>
          {!selected[0] && <div className="wheel-empty">Pairings follow the first ingredient</div>}
          {selected[0] && !firstRef && <div className="wheel-empty">A house batch has no pairings of its own</div>}
          {pairingsError && <div className="wheel-empty">{pairingsError}</div>}
          {firstRef && !pairingsError && pairings?.ref !== firstRef && <div className="wheel-empty">Loading…</div>}
          <div className="wheel-pairings">
            {goesWith.slice(0, 24).map((p) => {
              const pick: Pick = p.held[0]
                ? { kind: "library", ingredientId: p.held[0].ingredientId }
                : { kind: "database", name: p.name };
              const already = picks.some((x) => pickKey(x) === pickKey(pick));
              return (
                <button
                  key={p.name}
                  className={p.held.length ? "wheel-pairing held" : "wheel-pairing"}
                  disabled={full || already}
                  onClick={() => addPick(pick)}
                >
                  <span className="n">{p.held[0]?.name ?? p.name}</span>
                  <span className="s">
                    {p.held.length ? "in your library · " : ""}
                    {Math.round(p.synergy)}% synergy
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {refs.length >= 2 && (
          <section className="wheel-section">
            <div className="label">Synergy</div>
            <SynergyLine state={synergy?.key === refsKey ? synergy.result : null} />
          </section>
        )}

        <section className="wheel-section">
          <div className="label">
            Real cocktails{cocktails && cocktails.key === refsKey ? ` · ${cocktails.total}` : ""}
          </div>
          {refs.length === 0 && <div className="wheel-empty">Cocktails from real menus using the selection</div>}
          {cocktailsError && <div className="wheel-empty">{cocktailsError}</div>}
          {cocktails && cocktails.key === refsKey && cocktails.total === 0 && (
            <div className="wheel-empty">No recorded cocktail uses all of these</div>
          )}
          <div className="wheel-cocktails">
            {cocktails?.key === refsKey &&
              cocktails.list.map((c) => (
                <div key={c.id} className="wheel-cocktail">
                  <span className="n">{c.name}</span>
                  <span className="s">{[c.bar, c.city].filter(Boolean).join(" · ")}</span>
                </div>
              ))}
          </div>
          {cocktails?.key === refsKey && cocktails.list.length < cocktails.total && (
            <button
              className="btn sm"
              disabled={cocktailsBusy}
              onClick={() => void loadCocktails(refs, cocktails.list.length)}
            >
              {cocktailsBusy ? "Loading…" : "More"}
            </button>
          )}
        </section>

        {hasFeature(app.me, "chat") && (
          <div className="wheel-panel-foot">
            <button
              className="btn primary"
              disabled={drawn.length === 0 || !app.me.can["chat:use"]}
              onClick={() => app.go({ kind: "chat", params: { prompt } })}
            >
              Send to development chat
            </button>
          </div>
        )}
      </aside>

      {picking && (
        <NamePicker
          title="Add an ingredient to the wheel"
          names={allNames}
          namesError={namesError}
          library={app.library.ingredients
            .filter((i) => resolveFlavour(i.ingredientId, ingredients, specs, known) !== null)
            .sort((a, b) => a.name.localeCompare(b.name))}
          libraryLoading={libraryLoading}
          heldByRef={heldByRef}
          isPicked={(p) => picks.some((x) => pickKey(x) === pickKey(p))}
          onPick={(p) => {
            addPick(p);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      )}

      {linking !== null && (
        <NamePicker
          title={`Link ${ingredients.get(linking)?.name ?? "this ingredient"} to the flavour database`}
          names={allNames}
          namesError={namesError}
          library={null}
          libraryLoading={false}
          heldByRef={heldByRef}
          isPicked={() => false}
          onPick={(p) => {
            const ing = ingredients.get(linking);
            setLinking(null);
            if (ing && p.kind === "database") void saveLink(ing, p.name);
          }}
          onClose={() => setLinking(null)}
        />
      )}
    </div>
  );
}

/** The line under a pick's name: where its flavour comes from, and whether the bar holds it. */
function pickStatus(s: Selected): string {
  if (s.loading) return "loading…";
  if (!s.resolved) return "no flavour data";
  if (s.resolved.from === "batch") return `blended from its build · ${Math.round(s.resolved.coverage * 100)}% covered`;
  if (!s.held) return "not in your library";
  return s.ingredient?.flavour?.status === "suggested" ? "in your library · suggested link" : "in your library";
}

function SynergyLine({ state }: { readonly state: SynergyResult | "error" | null }) {
  if (state === null) return <div className="wheel-empty">Loading…</div>;
  // Live fetching is switched off for now; whatever stops a score arriving,
  // the panel says so quietly rather than showing an error.
  if (state === "error" || state.status === "off") {
    return <div className="wheel-quiet">Synergy scores aren't switched on yet</div>;
  }
  if (state.status === "unavailable") {
    return <div className="wheel-quiet">No synergy score for these yet</div>;
  }
  const { pairSynergy, trioSynergy, sharedTriadCount } = state.synergy;
  return (
    <div className="wheel-synergy">
      {pairSynergy !== null && (
        <span>
          <span className="v">{Math.round(pairSynergy)}%</span> pair synergy
        </span>
      )}
      {trioSynergy !== null && (
        <span>
          <span className="v">{Math.round(trioSynergy)}%</span> trio synergy
        </span>
      )}
      {sharedTriadCount > 0 && <span className="muted">{sharedTriadCount} shared three-note harmonies</span>}
    </div>
  );
}

interface NamePickerProps {
  readonly title: string;
  /** Every flavour-database name; null while loading. */
  readonly names: readonly string[] | null;
  readonly namesError: string | null;
  /** The bar's own ingredients to offer first, or null to offer only the flavour database names. */
  readonly library: readonly Ingredient[] | null;
  readonly libraryLoading: boolean;
  readonly heldByRef: ReadonlyMap<string, readonly Ingredient[]>;
  readonly isPicked: (pick: Pick) => boolean;
  readonly onPick: (pick: Pick) => void;
  readonly onClose: () => void;
}

/** Search the library and the flavour database's names, as the reference wheel's search does: prefix matches first. */
function NamePicker({ title, names, namesError, library, libraryLoading, heldByRef, isPicked, onPick, onClose }: NamePickerProps) {
  const [query, setQuery] = useState("");
  const q = flavourNameKey(query);
  const mine = library
    ? [...library.filter((i) => flavourNameKey(i.name).startsWith(q)), ...library.filter((i) => !flavourNameKey(i.name).startsWith(q) && flavourNameKey(i.name).includes(q))]
    : [];
  const theirs = names ? searchFlavourNames(names, query) : [];

  return (
    <div className="wheel-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div>
        <div className="wheel-modal-head">
          <h2>{title}</h2>
          <span className="spacer" />
          <button className="wheel-icon" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="wheel-modal-search">
          <input
            type="text"
            autoFocus
            placeholder="Search by name — lime, rye, Campari…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="wheel-modal-rows">
          {library && (
            <>
              <div className="label label-sm">Your library</div>
              {libraryLoading && <div className="wheel-empty">Loading flavour data…</div>}
              {!libraryLoading && mine.length === 0 && (
                <div className="wheel-empty">{q ? "Nothing in your library matches" : "Nothing in your library has flavour data yet"}</div>
              )}
              {mine.slice(0, 40).map((i) => {
                const pick: Pick = { kind: "library", ingredientId: i.ingredientId };
                return (
                  <button key={i.ingredientId} className="wheel-pick-row" disabled={isPicked(pick)} onClick={() => onPick(pick)}>
                    <span className="n">{i.name}</span>
                    <span className="s">
                      {i.flavour ? `${i.flavour.ref}${i.flavour.status === "suggested" ? " · suggested" : ""}` : "blended from its build"}
                    </span>
                  </button>
                );
              })}
            </>
          )}
          <div className="label label-sm">The whole flavour database</div>
          {namesError && <div className="msg err">{namesError}</div>}
          {!names && !namesError && <div className="wheel-empty">Loading names…</div>}
          {names && !q && <div className="wheel-empty">Type to search {names.length.toLocaleString()} ingredients</div>}
          {names && q && theirs.length === 0 && <div className="wheel-empty">Nothing in the flavour database matches</div>}
          {theirs.map((name) => {
            const pick: Pick = { kind: "database", name };
            const held = heldByRef.get(flavourNameKey(name)) ?? [];
            return (
              <button key={name} className="wheel-pick-row" disabled={isPicked(pick)} onClick={() => onPick(pick)}>
                <span className="n">{name}</span>
                <span className="s">{held.length ? `in your library as ${held.map((h) => h.name).join(", ")}` : "not in your library"}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
